import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material'
import { Polyline, useMap } from 'react-leaflet'
import { MapContainer } from '@components/map/MapContainer'
import type { RouteReportEvidence } from '@api/types/tickets'

/**
 * The published route with the reporter's walked track drawn over it.
 *
 * This is the whole point of asking for the track: a description like "the path is wrong after
 * the bridge" is hard to act on, while two lines that agree for a mile and then diverge tell a
 * reviewer exactly where the route stopped matching the ground, and what the walker did instead.
 *
 * The two are drawn in deliberately unlike styles rather than two colours. Where the lines lie on
 * top of each other — which is most of their length, and the part you want to skim past — a solid
 * line under a dashed one still reads as two things, where two solid lines of similar weight read
 * as one.
 */

const ROUTE_COLOR = '#0277bd'
const TRACK_COLOR = '#c62828'

function FitToEvidence({ bounds }: { bounds: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    if (bounds.length === 0) return
    map.fitBounds(bounds, { padding: [32, 32] })
    // Keyed on the point count rather than the array: a new array each render would refit the
    // map continuously and fight the reviewer trying to pan around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, bounds.length])
  return null
}

export function RouteEvidenceMap({ evidence }: { evidence: RouteReportEvidence }) {
  const [showRoute, setShowRoute] = useState(true)
  const [showTrack, setShowTrack] = useState(true)
  const [trackIndex, setTrackIndex] = useState(0)

  const track = evidence.tracks[trackIndex]

  const trackLine = useMemo<[number, number][]>(
    () => (track ? track.points.map((p) => [p.lat, p.lng] as [number, number]) : []),
    [track]
  )

  const routeLines = useMemo(
    () => evidence.sections.filter((s) => s.points.length > 1).map((s) => s.points),
    [evidence.sections]
  )

  const allPoints = useMemo<[number, number][]>(
    () => [...routeLines.flat(), ...trackLine],
    [routeLines, trackLine]
  )

  if (allPoints.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nothing to draw — this report has neither a published route nor a recorded track.
        </Typography>
      </Paper>
    )
  }

  const durationLabel = track?.durationMs
    ? `${Math.round(track.durationMs / 60000)} min`
    : null

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControlLabel
          control={<Switch checked={showRoute} onChange={(e) => setShowRoute(e.target.checked)} size="small" />}
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 18, height: 3, bgcolor: ROUTE_COLOR, borderRadius: 1 }} />
              <Typography variant="body2">Published route</Typography>
            </Stack>
          }
        />
        <FormControlLabel
          control={<Switch checked={showTrack} onChange={(e) => setShowTrack(e.target.checked)} size="small" />}
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 18,
                  height: 0,
                  borderTop: `3px dashed ${TRACK_COLOR}`,
                }}
              />
              <Typography variant="body2">What they walked</Typography>
            </Stack>
          }
        />

        {/* Only offered when there is a choice to make. */}
        {evidence.tracks.length > 1 &&
          evidence.tracks.map((t, i) => (
            <Chip
              key={t.sessionId}
              size="small"
              label={new Date(t.recordedAt).toLocaleDateString()}
              color={i === trackIndex ? 'primary' : 'default'}
              variant={i === trackIndex ? 'filled' : 'outlined'}
              onClick={() => setTrackIndex(i)}
            />
          ))}
      </Stack>

      {/* vh on a phone: a fixed 460px is most of a 640px screen and leaves no room to scroll past it. */}
      <Box sx={{ height: { xs: '55vh', sm: 460 }, minHeight: 280, borderRadius: 1, overflow: 'hidden' }}>
        <MapContainer height="100%" zoom={13}>
          <FitToEvidence bounds={allPoints} />

          {showRoute &&
            routeLines.map((points, i) => (
              <Polyline
                key={`route-${evidence.sections[i]?.sectionId ?? i}`}
                positions={points}
                pathOptions={{ color: ROUTE_COLOR, weight: 5, opacity: 0.85 }}
              />
            ))}

          {showTrack && trackLine.length > 1 && (
            <Polyline
              positions={trackLine}
              pathOptions={{ color: TRACK_COLOR, weight: 3, opacity: 0.95, dashArray: '6 6' }}
            />
          )}
        </MapContainer>
      </Box>

      <Typography variant="caption" color="text.secondary">
        {track
          ? `${track.pointCount.toLocaleString()} fixes recorded ${new Date(
              track.recordedAt
            ).toLocaleString()}${durationLabel ? ` over ${durationLabel}` : ''}.`
          : 'No track was attached to this report.'}
      </Typography>
    </Stack>
  )
}
